import React, { FormEvent, useEffect, useState } from 'react';
import { colors, Popover } from '@dexma/ui-components';
import {
    StyledCustomizablePeriod,
    StyledDateRange,
    StyledCalendarSection,
    StyledFormColumn,
    StyledNumberInput,
    StyledPeriodSelectorUI,
    StyledPeriodSelect,
} from 'adapter/in/common/components/PeriodSelector/style';
import { subDays, isToday, isAfter, isBefore, addYears, subYears } from 'date-fns';
import SelectorValue from 'domain/entity/preset/field/selectorValue';
import i18n from 'asset/translation';
import { RangeKeyDict } from 'react-date-range';
import { transparentize } from 'polished';
import {
    areFixedStatesEqual,
    areRollingStatesEqual,
    formatFixedDateLabel,
    formatRollingLabel,
    parseCalendarStateToFormState,
    parseFormStateToCalendarState,
} from 'adapter/in/common/components/PeriodSelector/periodSelectorUtils';
import {
    FakeSelector,
    PeriodTypeFieldGroup,
    SelectablePeriodTemplates,
    SwitchGroup,
} from 'adapter/in/common/components/PeriodSelector/helperComponents';
import gtm from 'adapter/in/common/config/gtm';
import { PeriodFormState, PeriodSelectorPeriods } from 'adapter/in/common/form/types';

const PERIOD_OPTIONS = [
    { value: PeriodSelectorPeriods.DAY, label: 'common.periods.days' },
    { value: PeriodSelectorPeriods.WEEK, label: 'common.periods.weeks' },
    { value: PeriodSelectorPeriods.MONTH, label: 'common.periods.months' },
    { value: PeriodSelectorPeriods.QUARTER, label: 'common.periods.quarters' },
    { value: PeriodSelectorPeriods.SEMESTER, label: 'common.periods.semesters' },
    { value: PeriodSelectorPeriods.YEAR, label: 'common.periods.years' },
];

export enum PeriodType {
    ROLLING = 'ROLLING',
    FIXED = 'FIXED',
}

type RollingPeriodSelectorUICallbackState = {
    type: PeriodType.ROLLING;
} & PeriodFormState;

export type FixedPeriodSelectorUICallbackState = {
    type: PeriodType.FIXED;
    isToEnd: boolean;
} & CalendarState;

export type PeriodSelectorUICallbackState = RollingPeriodSelectorUICallbackState | FixedPeriodSelectorUICallbackState;

export type CalendarState = { startDate: Date; endDate: Date };

export type SelectablePeriodTemplateState = { state: PeriodFormState } & SelectorValue;

export type PeriodSelectorTexts = {
    periodTypeTitle: string;
    periodTypeRollingLabel: string;
    periodTypeFixedLabel: string;
    periodTypeRollingText: string;
    periodTypeFixedText: string;
    customizablePeriodTitle: string;
    currentPeriodTitle: string;
    untilTheEndTitle: string;
};

export const PERIOD_SELECTOR_TEXTS: PeriodSelectorTexts = {
    periodTypeTitle: 'common.periodSelector.periodTypeTitle',
    periodTypeFixedLabel: 'common.periodSelector.periodSelectorTypes.fixed',
    periodTypeRollingLabel: 'common.periodSelector.periodSelectorTypes.rolling',
    periodTypeFixedText: 'common.periodSelector.periodSelectorTypes.fixedText',
    periodTypeRollingText: 'common.periodSelector.periodSelectorTypes.rollingText',
    customizablePeriodTitle: 'common.periodSelector.customizablePeriodTitle',
    untilTheEndTitle: 'common.periodSelector.untilTheEndTitle',
    currentPeriodTitle: 'common.periodSelector.currentPeriodTitle',
};

export type PeriodSelectorUIprops = {
    onChange: (calendar: PeriodSelectorUICallbackState) => void;
    defaultValue?: PeriodSelectorUICallbackState;
    disabled?: boolean;
    texts: PeriodSelectorTexts;
    templatePeriods?: SelectablePeriodTemplateState[];
    primaryColor: string;
    dateFormat?: string;
    referenceDate?: Date;
};
const PeriodSelector = (props: PeriodSelectorUIprops) => {
    const {
        onChange: submit,
        texts,
        templatePeriods: periodValues,
        primaryColor,
        dateFormat,
        defaultValue: controlledState,
        disabled,
        referenceDate,
    } = props;
    const controlledReferenceDate = referenceDate || new Date();
    const minDate = subYears(controlledReferenceDate, 10);
    const maxDate = addYears(controlledReferenceDate, 1);
    const [periodType, setPeriodType] = useState<PeriodType>(PeriodType.ROLLING);
    const [calendarState, setCalendarState] = useState<CalendarState>({
        startDate: subDays(controlledReferenceDate, 7),
        endDate: controlledReferenceDate,
    });

    // number is a Number, but we will use 0 for a special disabled state, to not propagate
    // nulls al over the code. Also, it i possible "NaN" when user inputs ""
    const [number, setNumber] = useState(1);
    const [period, setPeriod] = useState(PeriodSelectorPeriods.DAY);
    const [isWithCurrent, setIsWithCurrent] = useState(false);
    const [isToEnd, setIsToEnd] = useState(false);
    const [isUIOpen, setIsUIOpen] = useState<boolean>(false);
    const [label, setLabel] = useState<string>();

    useEffect(() => {
        if (
            controlledState &&
            controlledState.type === PeriodType.ROLLING &&
            !areRollingStatesEqual(controlledState, { number, isToEnd, isWithCurrent, period })
        ) {
            setPeriodType(PeriodType.ROLLING);
            updateFormState(controlledState);
            const calendar = parseFormStateToCalendarState(
                controlledState.number,
                controlledState.period,
                controlledState.isWithCurrent,
                controlledReferenceDate
            );
            setCalendarState({
                endDate: calendar.endDate,
                startDate: calendar.startDate,
            });
        }
        if (
            controlledState &&
            controlledState.type === PeriodType.FIXED &&
            !areFixedStatesEqual(controlledState, { ...calendarState, isToEnd })
        ) {
            setPeriodType(PeriodType.FIXED);
            setCalendarState({ startDate: controlledState.startDate, endDate: controlledState.endDate });
            updateFormState(
                parseCalendarStateToFormState(
                    {
                        startDate: controlledState.startDate,
                        endDate: controlledState.endDate,
                    },
                    controlledReferenceDate
                )
            );
        }
    }, [controlledState, periodValues]);

    useEffect(() => {
        if (controlledState?.type === PeriodType.ROLLING) {
            setLabel(formatRollingLabel(controlledState, periodValues));
        }
        if (controlledState?.type === PeriodType.FIXED) {
            setLabel(formatFixedDateLabel(controlledState, dateFormat));
        }
    }, [controlledState, periodValues]);

    const handleSubmit = (formState: PeriodFormState, calendar: CalendarState, periodTypeParam: PeriodType) => {
        if (periodTypeParam === PeriodType.ROLLING) {
            submit({ ...formState, type: PeriodType.ROLLING });
            setLabel(formatRollingLabel(formState, periodValues));
        }
        if (periodTypeParam === PeriodType.FIXED) {
            submit({ endDate: calendar.endDate, startDate: calendar.startDate, type: PeriodType.FIXED, isToEnd: formState.isToEnd });
            setLabel(formatFixedDateLabel({ ...calendar, isToEnd: formState.isToEnd }, dateFormat));
        }
    };

    const handleChangeCalendar = (ranges: RangeKeyDict) => {
        if (ranges.range1.startDate && ranges.range1.endDate) {
            const calendar = { startDate: ranges.range1.startDate, endDate: ranges.range1.endDate };
            setCalendarState({
                startDate: calendar.startDate,
                endDate: calendar.endDate,
            });
            handleSubmit({ number, period, isToEnd, isWithCurrent }, calendar, PeriodType.FIXED);
            updateFormState(
                parseCalendarStateToFormState(
                    {
                        startDate: calendar.startDate,
                        endDate: calendar.endDate,
                    },
                    controlledReferenceDate
                )
            );
        }
    };

    const handleChangePeriodType = (periodTypeParam: PeriodType) => {
        setPeriodType(periodTypeParam);
        if (number === 0 && periodTypeParam === PeriodType.ROLLING) {
            setNumber(1);
            handleSubmit({ number: 1, period, isToEnd, isWithCurrent }, calendarState, periodTypeParam);
            const calendar = parseFormStateToCalendarState(1, period, isWithCurrent, controlledReferenceDate);
            setCalendarState({
                endDate: calendar.endDate,
                startDate: calendar.startDate,
            });
        } else if (Number.isNaN(number) && periodTypeParam === PeriodType.FIXED) {
            const parsedCalendarState = parseCalendarStateToFormState(
                {
                    startDate: calendarState.startDate,
                    endDate: calendarState.endDate,
                },
                controlledReferenceDate
            );
            setNumber(parsedCalendarState.number);
            handleSubmit(parsedCalendarState, calendarState, periodTypeParam);
        } else {
            handleSubmit({ number, period, isToEnd, isWithCurrent }, calendarState, periodTypeParam);
        }
    };

    const handleChangeForm = (formState: PeriodFormState) => {
        const isValidNumber = formState.number !== 0 && !Number.isNaN(formState.number);
        const userChangesFormWithNoValidNumber = Number.isNaN(number) && Number.isNaN(formState.number);

        if (isValidNumber) {
            const calendar = parseFormStateToCalendarState(
                formState.number,
                formState.period,
                formState.isWithCurrent,
                controlledReferenceDate
            );

            if (isAfter(calendar.startDate, minDate)) {
                setCalendarState({
                    endDate: calendar.endDate,
                    startDate: calendar.startDate,
                });
                updateFormState(formState);
                handleSubmit(formState, calendarState, periodType);
            }
        } else if (userChangesFormWithNoValidNumber) {
            const lastValidFormStateNumber = parseCalendarStateToFormState(calendarState, controlledReferenceDate).number;
            handleChangeForm({ ...formState, number: lastValidFormStateNumber });
        } else {
            // Set Number in case it is 0 or NaN, also allow to set istoEnd while FIXED
            // if it was clicked with no Rolling Match
            setNumber(formState.number);
            if (formState.isToEnd !== isToEnd) {
                setIsToEnd(!formState.isWithCurrent ? true : formState.isToEnd);
                handleSubmit(formState, calendarState, periodType);
            }
        }
    };

    const updateFormState = (formState: PeriodFormState) => {
        if (number !== formState.number) {
            setNumber(formState.number);
        }
        if (period !== formState.period) {
            setPeriod(formState.period);
        }
        if (isWithCurrent !== formState.isWithCurrent) {
            setIsWithCurrent(formState.isWithCurrent);
        }
        if (isToEnd !== formState.isToEnd) {
            setIsToEnd(!formState.isWithCurrent ? true : formState.isToEnd);
        }
    };

    const handlePopoverClick = () => {
        setIsUIOpen(!isUIOpen);
        if (isUIOpen && Number.isNaN(number)) {
            const parsedCalendarState = parseCalendarStateToFormState(
                {
                    startDate: calendarState.startDate,
                    endDate: calendarState.endDate,
                },
                controlledReferenceDate
            );
            setNumber(parsedCalendarState.number);
        }
    };

    let rangeColor = periodType === PeriodType.ROLLING ? transparentize(0.5, primaryColor) : primaryColor;
    const secondaryColor = periodType === PeriodType.ROLLING ? transparentize(0.5, colors.gray400) : colors.gray400;

    // Trick related to the underline color underneath "rdrDayToday"
    if (isAfter(calendarState.startDate, controlledReferenceDate) && !isToEnd) {
        rangeColor = secondaryColor;
    }

    const isDateRangeDisabled = periodType === PeriodType.ROLLING;

    const PeriodSelectorUI = () => (
        <StyledPeriodSelectorUI>
            <StyledFormColumn>
                <PeriodTypeFieldGroup
                    title={i18n.t(texts.periodTypeTitle)}
                    handleChange={handleChangePeriodType}
                    selectedPeriodType={periodType}
                    texts={texts}
                />
                <FormSection
                    formState={{ number, period, isWithCurrent, isToEnd }}
                    primaryColor={primaryColor}
                    periodType={periodType}
                    texts={texts}
                    periodValues={periodValues}
                    handleChange={handleChangeForm}
                    fixedHasNoRollingMatchAndIsInThePast={
                        isBefore(calendarState.endDate, controlledReferenceDate) &&
                        !isToday(calendarState.endDate) &&
                        periodType === PeriodType.FIXED
                    }
                />
            </StyledFormColumn>
            <StyledCalendarSection $isDateRangeDisabled={isDateRangeDisabled}>
                <StyledDateRange
                    editableDateInputs={!isDateRangeDisabled}
                    primaryColor={rangeColor}
                    secondaryColor={secondaryColor}
                    isDateRangeDisabled={isDateRangeDisabled}
                    onChange={handleChangeCalendar}
                    scroll={{ enabled: true }}
                    dateDisplayFormat={dateFormat || 'MMM d, yyyy'}
                    weekStartsOn={1}
                    minDate={minDate}
                    maxDate={maxDate}
                    isToEnd={isToEnd}
                    dayContentRenderer={(dayContent: Date) => (
                        <span className={isAfter(dayContent, controlledReferenceDate) ? 'rdrDayFuture' : undefined}>
                            {dayContent.getDate()}
                        </span>
                    )}
                    numberOfElements={periodValues?.length || 9}
                    ranges={[{ startDate: calendarState.startDate, endDate: calendarState.endDate, color: rangeColor }]}
                    direction='vertical'
                />
            </StyledCalendarSection>
        </StyledPeriodSelectorUI>
    );

    return disabled ? (
        <FakeSelector color={primaryColor} disabled={disabled} />
    ) : (
        <Popover placement='topLeft' trigger='click' content={PeriodSelectorUI} onOpenChange={handlePopoverClick}>
            <div>
                <FakeSelector
                    dataId={gtm.periodSelector.openPeriod}
                    label={label}
                    color={primaryColor}
                    isUIOpen={isUIOpen}
                    disabled={disabled}
                />
            </div>
        </Popover>
    );
};

type FormSectionProps = {
    texts: PeriodSelectorTexts;
    periodValues?: SelectablePeriodTemplateState[];
    handleChange: (formState: PeriodFormState) => void;
    primaryColor: string;
    formState: PeriodFormState;
    periodType: PeriodType;
    fixedHasNoRollingMatchAndIsInThePast: boolean;
};
const FormSection = (props: FormSectionProps) => {
    const {
        texts,
        periodValues,
        handleChange,
        primaryColor,
        formState,
        periodType,

        fixedHasNoRollingMatchAndIsInThePast: fixedHasNoMatchAndIsInThePast,
    } = props;

    return (
        <div>
            <SelectablePeriodTemplates
                primaryColor={primaryColor}
                periodValues={periodValues}
                areDisabled={periodType === PeriodType.FIXED}
                handleChange={(templateState: PeriodFormState) => {
                    handleChange({
                        number: templateState.number,
                        period: templateState.period,
                        isWithCurrent: templateState.isWithCurrent,
                        isToEnd: templateState.isToEnd,
                    });
                }}
                formState={formState}
            />
            <StyledCustomizablePeriod>
                <span className='customizable-period-title'>{i18n.t(texts.customizablePeriodTitle)}</span>
                <StyledNumberInput
                    dataId={gtm.periodSelector.input}
                    type={formState.number === 0 ? 'text' : 'number'}
                    min={1}
                    value={formState.number === 0 ? '-' : formState.number.toString()}
                    disabled={periodType === PeriodType.FIXED}
                    onChange={(e: FormEvent<HTMLInputElement>) => {
                        e.preventDefault();
                        // This if is to avoid the second empty value click when value is already empty
                        if (!Number.isNaN(formState.number) || !Number.isNaN(parseFloat(e.currentTarget.value))) {
                            handleChange({
                                ...formState,
                                number: parseFloat(e.currentTarget.value),
                            });
                        }
                    }}
                />
                <StyledPeriodSelect
                    mode='single'
                    allowClear={false}
                    dataId={gtm.periodSelector.select}
                    options={PERIOD_OPTIONS.map((option) => ({ ...option, label: i18n.t(option.label) }))}
                    disabled={periodType === PeriodType.FIXED}
                    value={formState.number === 0 ? '-' : formState.period}
                    onChange={(e: PeriodSelectorPeriods) => {
                        handleChange({ ...formState, period: e });
                    }}
                />
                <SwitchGroup
                    id='withCurrent'
                    title={i18n.t(texts.currentPeriodTitle)}
                    isChecked={formState.isWithCurrent}
                    $isDisabled={periodType === PeriodType.FIXED}
                    onChange={() => {
                        handleChange({
                            ...formState,
                            isWithCurrent: !formState.isWithCurrent,
                        });
                    }}
                />
                <SwitchGroup
                    id='toEnd'
                    title={i18n.t(texts.untilTheEndTitle)}
                    isChecked={formState.isToEnd}
                    $isDisabled={(!formState.isWithCurrent && periodType === PeriodType.ROLLING) || fixedHasNoMatchAndIsInThePast}
                    onChange={() => {
                        handleChange({ ...formState, isToEnd: !formState.isToEnd });
                    }}
                />
            </StyledCustomizablePeriod>
        </div>
    );
};

export default PeriodSelector;
